import React from 'react';
import { Link } from 'react-router-dom';

import ResetPasswordForm from '../../components/auth/ResetPasswordForm';
import LogoCarousel from '../../components/shared/LogoCarousel';

/**
 * @page ResetPassword
 * @description Página para solicitar recuperación de contraseña
 * @vibe-color 🟠 #F59E0B
 * @vibe-icon 🔑
 */
const ResetPassword: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Panel lateral con información y carrusel */}
        <div className="hidden lg:block relative w-1/2">
          <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
            <div className="flex h-full flex-col items-center justify-center p-10 text-center">
              {/* Logo NFCores principal */}
              <div className="mb-8">
                <Link to="/" className="flex items-center justify-center space-x-3">
                  <img
                    className="h-16 w-auto"
                    src="/logo.svg"
                    alt="NFCores Logo"
                    onError={(e) => {
                      e.currentTarget.src = '/assets/images/NFCores-original.png';
                    }}
                  />
                  <div className="text-4xl font-bold text-white">NFCores</div>
                </Link>
              </div>

              {/* Información principal */}
              <div className="max-w-md mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Recupera tu acceso
                </h2>
                <p className="text-xl text-orange-100 leading-relaxed">
                  No te preocupes, es normal olvidar las contraseñas. Te ayudamos a recuperar el acceso a tu cuenta de forma segura.
                </p>
              </div>

              {/* Carrusel de logos de submarcas */}
              <div className="w-full max-w-sm mb-12">
                <LogoCarousel autoPlay={true} interval={4000} />
              </div>

              {/* Pasos de recuperación */}
              <div className="grid grid-cols-1 gap-4 text-center max-w-xs">
                <div className="flex items-center space-x-3 text-white">
                  <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <p className="text-sm">Ingresa tu email</p>
                </div>
                <div className="flex items-center space-x-3 text-white">
                  <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <p className="text-sm">Revisa tu bandeja</p>
                </div>
                <div className="flex items-center space-x-3 text-white">
                  <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <p className="text-sm">Crea nueva contraseña</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formulario de recuperación */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            {/* Logo para móviles */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="flex items-center justify-center space-x-3">
                <img
                  className="h-12 w-auto"
                  src="/logo.svg"
                  alt="NFCores Logo"
                  onError={(e) => {
                    e.currentTarget.src = '/assets/images/NFCores-original.png';
                  }}
                />
                <div className="text-3xl font-bold text-primary-600">NFCores</div>
              </Link>
            </div>

            {/* Título */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Recupera tu cuenta
              </h2>
              <p className="text-gray-600">
                Restablece la contraseña de tu cuenta NFCores
              </p>
            </div>

            {/* Información de ayuda */}
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-orange-800">
                    Te enviaremos un enlace a tu correo electrónico para que puedas restablecer tu contraseña de forma segura.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Formulario */}
            <ResetPasswordForm />

            {/* Enlaces */}
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600">
                ¿Recordaste tu contraseña?{' '}
                <Link to="/auth/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Volver al login
                </Link>
              </p>
              <p className="text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <Link to="/auth/register" className="font-medium text-primary-600 hover:text-primary-500">
                  Regístrate aquí
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
